import React, { useMemo, useState } from 'react';

import { Box, LinearProgress, Paper, Typography } from '@mui/material';
import { Spacer } from 'src/components/generic';
import {
  BlueAlineIcon,
  GetStartedHeaderImage,
  LaunchNDAIcon,
  SetUpYourRepositoryWithAlineAIIcon,
  SignAThirdPartyPdfIcon,
} from 'src/components/icons/GetStarted';
import { useSelectCurrentUser } from 'src/gql';
import { makeStyles } from 'tss-react/mui';

import { GetStartedFooter } from './GetStartedFooter';
import type { OnboardingStep } from './onboarding';
import { QuickStartTitle, useOnboarding } from './onboarding';
import { StepAccordion } from './StepAccordion';

export const onboardingSectionWidthPx = 835;
const useStyles = makeStyles()(theme => ({
  bgWhite: {
    backgroundColor: '#FFFF',
  },
  boldMsg: {
    fontWeight: 600,
  },
  button: {
    margin: theme.spacing(1, 0),
  },
  expandableSection: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    justifyContent: 'center',
    padding: theme.spacing(0, 3),
  },
  header: {
    alignItems: 'center',
    alignSelf: 'stretch',
    display: 'flex',
    gap: theme.spacing(4),
    padding: theme.spacing(0, 3),
  },
  paper: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'center',
    padding: theme.spacing(2.5, 0, 3, 0),
    width: onboardingSectionWidthPx,
    // zIndex higher so header image goes below section paper.
    zIndex: 10,
  },
  personIcon: {
    position: 'relative',
    top: 64,
  },
  progressBar: {
    width: '100%',
  },
  quickStartContainer: {
    alignItems: 'flex-end',
    boxSizing: 'border-box',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(2),
    paddingRight: theme.spacing(4),
    width: onboardingSectionWidthPx,
  },
  root: {
    backgroundColor: '#F8F9FA',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    overflow: 'auto',
    padding: theme.spacing(1),
    position: 'relative',
    width: '100%',
  },
  section: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'center',
    padding: theme.spacing(1, 0),
  },
  steps: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  textBubble: {
    border: '1px solid #FEDF89',
    borderRadius: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    width: 320,
  },
}));

export function GetStartedPage() {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const { loading, sections } = useOnboarding();

  if (loading)
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );

  return (
    <Box className={classes.root}>
      <Box className={classes.section}>
        <div className={classes.quickStartContainer}>
          <Typography align="left" variant="subtitle1">
            Quick Start
          </Typography>
          <Spacer />
          <div className={classes.textBubble}>
            <Typography color="textSecondary">Jay from Aline</Typography>
            <Typography
              className={classes.boldMsg}
            >{`Hello ${currentUser?.displayName}! I'm Jay and I'm here to help you get set up.`}</Typography>
          </div>
          <GetStartedHeaderImage className={classes.personIcon} />
        </div>
        {sections.quickStart.map(step => {
          return <AccordionWrapper key={step.title} step={step} />;
        })}
      </Box>
      <GetStartedFooter />
    </Box>
  );
}

function AccordionWrapper({ step }: { step: OnboardingStep }) {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <Paper key={step.title} className={classes.paper}>
      <StepHeader onboardingStep={step} />
      <Box className={classes.expandableSection}>
        {step.tasks.map((onboardingAction, i) => {
          return (
            <StepAccordion
              key={i}
              expanded={expanded}
              onboardingTask={onboardingAction}
              setExpanded={setExpanded}
              stepNum={i + 1}
            />
          );
        })}
      </Box>
    </Paper>
  );
}

function StepHeader({ onboardingStep }: { onboardingStep: OnboardingStep }) {
  const { classes } = useStyles();
  const tasksLeft = useMemo(() => {
    return onboardingStep.tasks.filter(t => !t.completedAt);
  }, [onboardingStep]);
  const timeLeft = useMemo(() => {
    return tasksLeft.reduce((acc, curr) => acc + curr.numMins, 0);
  }, [tasksLeft]);
  const progress = useMemo(() => {
    const completed = onboardingStep.tasks.filter(t => Boolean(t.completedAt));
    return (completed.length / onboardingStep.tasks.length) * 100;
  }, [onboardingStep]);
  const stepsLeftText =
    tasksLeft.length === 1 ? `${tasksLeft.length} step left` : `${tasksLeft.length} steps left`;
  const timeLeftText =
    timeLeft === 1 ? `About ${timeLeft} min left` : `About ${timeLeft} mins left`;

  return (
    <>
      <div className={classes.header}>
        <StepHeaderIcon stepTitle={onboardingStep.title} />
        <Typography variant="subtitle1">{onboardingStep.title}</Typography>
        <Spacer />
        <div className={classes.steps}>
          <Typography variant="subtitle2">
            {tasksLeft.length ? stepsLeftText : 'All steps complete'}
          </Typography>
          {Boolean(tasksLeft.length) && (
            <>
              <Typography>•</Typography>
              <Typography>{timeLeftText}</Typography>
            </>
          )}
        </div>
      </div>
      <LinearProgress className={classes.progressBar} value={progress} variant="determinate" />
    </>
  );
}

function StepHeaderIcon({ stepTitle }: { stepTitle: QuickStartTitle }) {
  switch (stepTitle) {
    case QuickStartTitle.GetToKnowAline:
      return <BlueAlineIcon />;
    case QuickStartTitle.AIPlaybooks:
      return <LaunchNDAIcon />;
    case QuickStartTitle.AlineWorkflows:
      return <SignAThirdPartyPdfIcon />;
    case QuickStartTitle.SetUpAIRepository:
      return <SetUpYourRepositoryWithAlineAIIcon />;
  }
}
